import { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Navbar } from '@/components/layout/Navbar';
import { useWebpages } from '@/contexts/WebpagesContext';
import type { Webpage } from '@/types';
import { formatDistanceToNow } from 'date-fns';
import { WebpageDetails } from '@/components/webpage/WebpageDetails';
import { WebpageCardSkeleton } from "@/components/webpage/WebpageCardSkeleton";
import { Skeleton } from "@/components/ui/skeleton";
import { Favicon } from "@/components/webpage/Favicon";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useMediaQuery } from "@/hooks/use-media-query";
import { AddWebpageFab } from "@/components/webpage/AddWebpageFab";
import { WebpageActionsFab } from "@/components/webpage/WebpageActionsFab";
import { DeleteWebpageDialog } from "@/components/webpage/DeleteWebpageDialog";
import { useToast } from "@/components/ui/use-toast";
import { ArrowDown, ArrowUp, ArrowElbowDownLeft } from "@phosphor-icons/react";
import { ChatDock } from '@/components/chat/ChatDock';
import type { ChatSource } from '@/lib/chat/types';
import { CHAT_ENABLED } from '@/lib/chat/config';
import { UsageBanner } from '@/components/saved/UsageBanner';
import { supabase } from '@/lib/supabase';
import { analytics } from '@/lib/analytics';

interface UsageBucket {
  used: number;
  limit: number | null;
}

interface UsageResponse {
  is_pro: boolean;
  saves: UsageBucket;
  ai_message: UsageBucket;
  mcp_call: UsageBucket;
  upgrade_url: string;
}

function cleanUrl(url: string): string {
  if (!url) return '';
  try {
    const urlObj = new URL(url);
    let cleanedUrl = urlObj.hostname.replace(/^www\./, '') + urlObj.pathname;
    cleanedUrl = cleanedUrl.replace(/\/$/, '');
    return cleanedUrl.length > 40 ? cleanedUrl.slice(0, 40) + '...' : cleanedUrl;
  } catch {
    return url.slice(0, 40);
  }
}

interface SavedProps {
  isInitialLoading?: boolean;
}

export function Saved({ isInitialLoading = false }: SavedProps) {
  const { 
    user, 
    webpages,
    isCountLoading,
    isHydratingAll,
    isLoading: contextLoading, 
    isLoadingMore, 
    hasMore,
    loadMore,
    prefetchForSearch,
    allLoaded,
    totalCount,
    search, 
    deleteWebpage,
    signOut 
  } = useWebpages();
  
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const isChatPrefillMode = searchParams.get('mode') === 'chat';
  const searchQuery = isChatPrefillMode ? '' : (searchParams.get('query') || '');
  const chatPrefillQuery = isChatPrefillMode ? ((searchParams.get('chatQuery') || searchParams.get('query') || '').trim()) : '';
  const chatPrefillRequestKey = chatPrefillQuery ? `${location.key}:${chatPrefillQuery}` : '';
  const chatFromUrlOpen = searchParams.get('chat') === '1' || isChatPrefillMode;
  const chatConversationFromUrl = searchParams.get('c');
  
  // UI state
  const [hoveredWebpage, setHoveredWebpage] = useState<Webpage | null>(null);
  const [selectedWebpage, setSelectedWebpage] = useState<Webpage | null>(null);
  const [mobileSelectedWebpage, setMobileSelectedWebpage] = useState<Webpage | null>(null);
  const [keyboardIndex, setKeyboardIndex] = useState<number>(-1);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  
  // Mobile edit/delete state
  const [mobileIsEditing, setMobileIsEditing] = useState(false);
  const [showMobileDeleteDialog, setShowMobileDeleteDialog] = useState(false);
  const [isMobileDeleting, setIsMobileDeleting] = useState(false);
  
  // Desktop edit/delete/dropdown state for keyboard shortcuts
  const [desktopIsEditing, setDesktopIsEditing] = useState(false);
  const [showDesktopDeleteDialog, setShowDesktopDeleteDialog] = useState(false);
  const [isDesktopDeleting, setIsDesktopDeleting] = useState(false);
  const [desktopDropdownOpen, setDesktopDropdownOpen] = useState(false);

  // Track dropdown open state to prevent deselection
  const isDropdownOpenRef = useRef(false);
  
  // Refs for webpage cards (for spring scroll animation)
  const cardRefsMap = useRef<Map<string, HTMLDivElement>>(new Map());
  
  // Ref to track pending selection after deletion
  const pendingDeleteIndexRef = useRef<number | null>(null);

  // Usage banner state (Saver-only, shows ≥80% of saves cap)
  const [usage, setUsage] = useState<UsageResponse | null>(null);

  const isMdOrLarger = useMediaQuery('(min-width: 768px)');
  const isLgOrLarger = useMediaQuery('(min-width: 1024px)');
  
  // Refs for infinite scroll
  const observerRef = useRef<IntersectionObserver | null>(null);
  // Use refs to access latest values inside IntersectionObserver callback
  const isLoadingMoreRef = useRef(isLoadingMore);
  const hasMoreRef = useRef(hasMore);
  
  // Keep refs in sync with state
  useEffect(() => {
    isLoadingMoreRef.current = isLoadingMore;
  }, [isLoadingMore]);
  
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);
  
  const loadMoreCallbackRef = useCallback((node: HTMLDivElement | null) => {
    // Disconnect previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    
    if (!node) return;
    if (searchQuery) return; // Don't observe when searching
    
    observerRef.current = new IntersectionObserver(
      (entries) => {
        // Use refs to get latest values (not stale closure values)
        if (entries[0].isIntersecting && hasMoreRef.current && !isLoadingMoreRef.current) {
          loadMore();
        }
      },
      { rootMargin: '400px' } // Trigger earlier
    );
    
    observerRef.current.observe(node);
  }, [loadMore, searchQuery]); // Removed hasMore and isLoadingMore - using refs instead

  const isLoading = isInitialLoading || contextLoading;
  
  // When search is active (e.g., direct navigation with ?query=), load all data
  useEffect(() => {
    if (!contextLoading && searchQuery && !allLoaded && hasMore) {
      prefetchForSearch();
    }
  }, [contextLoading, searchQuery, allLoaded, hasMore, prefetchForSearch]);

  // When landing in chat mode, preload all webpages to improve source recall.
  useEffect(() => {
    if (!contextLoading && chatFromUrlOpen && !allLoaded && hasMore) {
      prefetchForSearch();
    }
  }, [contextLoading, chatFromUrlOpen, allLoaded, hasMore, prefetchForSearch]);

  // Default /saved load pattern: first 10 quickly, then hydrate the rest in background.
  useEffect(() => {
    if (!contextLoading && !searchQuery && !chatFromUrlOpen && !allLoaded && hasMore) {
      prefetchForSearch();
    }
  }, [contextLoading, searchQuery, chatFromUrlOpen, allLoaded, hasMore, prefetchForSearch]);

  // Fetch billing usage to drive the cap banner. Re-runs when totalCount
  // changes so adds and deletes refresh the banner without a page reload.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;
      try {
        const res = await fetch('/api/billing/usage', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (!res.ok) return;
        const json = (await res.json()) as UsageResponse;
        if (!cancelled) setUsage(json);
      } catch {
        // Banner is best-effort; silently skip on network failures.
      }
    })();
    return () => { cancelled = true; };
  }, [totalCount]);

  // Show banner only for Saver users (is_pro=false) at ≥80% of saves cap.
  const showUsageBanner = Boolean(
    usage &&
    !usage.is_pro &&
    usage.saves.limit !== null &&
    usage.saves.used >= usage.saves.limit * 0.8
  );

  // Get filtered results
  const filteredWebpages = search(searchQuery);

  const lastTrackedQueryRef = useRef<string>('');

  // Sync selected webpages with updated data from context (for realtime updates after edit/save)
  useEffect(() => {
    if (mobileSelectedWebpage) {
      const updated = filteredWebpages.find(w => w.id === mobileSelectedWebpage.id);
      if (updated && updated !== mobileSelectedWebpage) {
        setMobileSelectedWebpage(updated);
      }
    }
    if (selectedWebpage) {
      const updated = filteredWebpages.find(w => w.id === selectedWebpage.id);
      if (updated && updated !== selectedWebpage) {
        setSelectedWebpage(updated);
      }
    }
  }, [filteredWebpages, mobileSelectedWebpage, selectedWebpage]);
  
  // Display count: total if not searching, filtered count if searching
  // Use Math.max to guard against totalCount being 0 while pages are loaded (race condition)
  const displayCount = searchQuery ? filteredWebpages.length : Math.max(totalCount, filteredWebpages.length);
  const shouldShowSearchSkeleton = Boolean(searchQuery && isHydratingAll && filteredWebpages.length === 0);
  const shouldShowListSkeleton = isLoading || shouldShowSearchSkeleton;
  const isCountPending = isCountLoading || shouldShowSearchSkeleton;

  // Search analytics: debounced so a stream of keystrokes doesn't fire dozens
  // of events — only the query the user paused on. zero_results fires alongside
  // when the query was non-empty but matched nothing.
  useEffect(() => {
    const q = searchQuery.trim();
    if (!q) return;
    if (shouldShowListSkeleton) return;
    if (q === lastTrackedQueryRef.current) return;
    const t = setTimeout(() => {
      if (q === lastTrackedQueryRef.current) return;
      lastTrackedQueryRef.current = q;
      analytics.track('search_run', {
        query_length: q.length,
        results_count: filteredWebpages.length,
      });
      if (filteredWebpages.length === 0) {
        analytics.track('search_zero_results', { query_length: q.length });
      }
    }, 600);
    return () => clearTimeout(t);
  }, [searchQuery, filteredWebpages.length, shouldShowListSkeleton]);

  const handleSearch = useCallback((query: string) => {
    const params = new URLSearchParams(searchParams);
    params.delete('mode');
    if (query) {
      params.set('query', query);
    } else {
      params.delete('query');
    }
    const queryString = params.toString();
    navigate(queryString ? `?${queryString}` : location.pathname, { replace: true });
  }, [location.pathname, navigate, searchParams]);

  // Animation ref for scroll
  const scrollAnimRef = useRef<number | null>(null);
  
  // Preload adjacent images for smoother navigation
  useEffect(() => {
    if (keyboardIndex < 0 || !filteredWebpages.length) return;
    
    // Preload images for adjacent items (prev, current, next)
    const indicesToPreload = [keyboardIndex - 1, keyboardIndex, keyboardIndex + 1, keyboardIndex + 2]
      .filter(i => i >= 0 && i < filteredWebpages.length);
    
    indicesToPreload.forEach(i => {
      const wp = filteredWebpages[i];
      if (wp?.image) {
        const img = new Image();
        img.src = wp.image;
      }
    });
  }, [keyboardIndex, filteredWebpages]);
  
  // Scroll card at index into view - fast and smooth
  const scrollToCardAtIndex = useCallback((index: number) => {
    const cardEl = document.querySelector(`[data-webpage-index="${index}"]`) as HTMLElement | null;
    if (!cardEl) return;
    
    const rect = cardEl.getBoundingClientRect();
    const navbarHeight = 56;
    const buffer = 300; // Comfortable buffer from edges (increased 50%)
    
    // Check if scroll needed
    const needsScrollUp = rect.top < navbarHeight + buffer;
    const needsScrollDown = rect.bottom > window.innerHeight - buffer;
    
    if (!needsScrollUp && !needsScrollDown) return;
    
    // Calculate target
    let targetY = window.scrollY;
    if (needsScrollUp) {
      targetY = window.scrollY + rect.top - navbarHeight - buffer;
    } else {
      targetY = window.scrollY + rect.bottom - window.innerHeight + buffer;
    }
    targetY = Math.max(0, targetY);
    
    // Cancel previous animation
    if (scrollAnimRef.current) cancelAnimationFrame(scrollAnimRef.current);
    
    // Fast ease-out animation (~150ms)
    const startY = window.scrollY;
    const distance = targetY - startY;
    const duration = 150;
    const startTime = performance.now();
    
    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-out cubic: 1 - (1 - t)^3
      const eased = 1 - Math.pow(1 - progress, 3);
      
      window.scrollTo(0, startY + distance * eased);
      
      if (progress < 1) {
        scrollAnimRef.current = requestAnimationFrame(animate);
      }
    };
    
    scrollAnimRef.current = requestAnimationFrame(animate);
  }, []);

  // Handle search bar focus - clear preview state and prefetch
  const handleSearchFocus = useCallback(() => {
    // Clear preview state when focusing search (but keep selected state)
    if (!selectedWebpage) {
      setHoveredWebpage(null);
      setKeyboardIndex(-1);
    }
    prefetchForSearch();
  }, [selectedWebpage, prefetchForSearch]);

  // Handle down arrow from search bar - select first item
  const handleSearchDownArrow = useCallback(() => {
    if (filteredWebpages.length > 0) {
      setKeyboardIndex(0);
      setHoveredWebpage(filteredWebpages[0]);
      // Scroll first card into view
      scrollToCardAtIndex(0);
    }
  }, [filteredWebpages, scrollToCardAtIndex]);

  const handleBeforeChatOpen = useCallback(() => {
    prefetchForSearch();
    if (!isLgOrLarger && mobileSelectedWebpage) {
      setMobileSelectedWebpage(null);
    }
  }, [prefetchForSearch, isLgOrLarger, mobileSelectedWebpage]);

  const handleChatOpenInAppSource = useCallback((source: ChatSource) => {
    const targetWebpage = webpages.find(webpage => webpage.id === source.webpageId);

    if (!targetWebpage) {
      prefetchForSearch();
      toast({
        title: 'Source not loaded yet',
        description: 'Loading more pages. Try again in a moment.',
      });
      return;
    }

    if (isLgOrLarger) {
      setSelectedWebpage(targetWebpage);
      setHoveredWebpage(null);
      const index = filteredWebpages.findIndex(webpage => webpage.id === targetWebpage.id);
      setKeyboardIndex(index);
      if (index >= 0) {
        scrollToCardAtIndex(index);
      }
      return;
    }

    setMobileSelectedWebpage(targetWebpage);
  }, [webpages, prefetchForSearch, toast, isLgOrLarger, filteredWebpages, scrollToCardAtIndex]);

  // Keep chat URL state in sync for deep linking and history restore.
  const handleChatStateChange = useCallback((state: { open: boolean; conversationId: string | null }) => {
    const params = new URLSearchParams(searchParams);
    let changed = false;

    const isLegacyChatPrefill = params.get('mode') === 'chat' && params.has('query');
    if (params.has('mode')) {
      params.delete('mode');
      changed = true;
    }
    if (params.has('chatQuery')) {
      params.delete('chatQuery');
      changed = true;
    }
    if (isLegacyChatPrefill && params.has('query')) {
      params.delete('query');
      changed = true;
    }

    if (state.open) {
      if (params.get('chat') !== '1') {
        params.set('chat', '1');
        changed = true;
      }
      if (state.conversationId) {
        if (params.get('c') !== state.conversationId) {
          params.set('c', state.conversationId);
          changed = true;
        }
      } else if (params.has('c')) {
        params.delete('c');
        changed = true;
      }
    } else {
      if (params.has('chat')) {
        params.delete('chat');
        changed = true;
      }
      if (params.has('c')) {
        params.delete('c');
        changed = true;
      }
    }

    if (changed) {
      const nextQuery = params.toString();
      navigate(nextQuery ? `?${nextQuery}` : location.pathname, { replace: true });
    }
  }, [location.pathname, navigate, searchParams]);

  // Desktop: handle webpage deleted - store index for next selection
  const handleDesktopWebpageDeleted = useCallback(() => {
    pendingDeleteIndexRef.current = keyboardIndex;
    setSelectedWebpage(null);
    setHoveredWebpage(null);
    setDesktopIsEditing(false);
  }, [keyboardIndex]);

  // Desktop: confirm delete via keyboard shortcut
  const handleDesktopDeleteConfirm = useCallback(async () => {
    if (!selectedWebpage) return;
    
    const currentIndex = keyboardIndex;
    
    setIsDesktopDeleting(true);
    const deletingId = selectedWebpage.id;
    try {
      await deleteWebpage(deletingId);
      setShowDesktopDeleteDialog(false);

      // Mark pending selection for effect to handle
      pendingDeleteIndexRef.current = currentIndex;
      setSelectedWebpage(null);
      setHoveredWebpage(null);

      analytics.track('save_deleted', { save_id: deletingId, via: 'desktop_shortcut' });
      toast({
        title: 'Deleted',
        description: 'Webpage removed successfully',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to delete webpage',
        variant: 'destructive',
      });
    } finally {
      setIsDesktopDeleting(false);
    }
  }, [selectedWebpage, keyboardIndex, deleteWebpage, toast]);

  // Mobile: handle edit action from FAB
  const handleMobileEdit = useCallback(() => {
    setMobileIsEditing(true);
  }, []);

  // Mobile: handle delete action from FAB
  const handleMobileDeleteRequest = useCallback(() => {
    setShowMobileDeleteDialog(true);
  }, []);

  // Mobile: confirm delete
  const handleMobileDeleteConfirm = useCallback(async () => {
    if (!mobileSelectedWebpage) return;
    
    setIsMobileDeleting(true);
    const deletingId = mobileSelectedWebpage.id;
    try {
      await deleteWebpage(deletingId);
      setShowMobileDeleteDialog(false);
      setMobileSelectedWebpage(null);
      analytics.track('save_deleted', { save_id: deletingId, via: 'mobile_actions' });
      toast({
        title: 'Deleted',
        description: 'Webpage removed successfully',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to delete webpage',
        variant: 'destructive',
      });
    } finally {
      setIsMobileDeleting(false);
    }
  }, [mobileSelectedWebpage, deleteWebpage, toast]);

  // Reset mobile edit state when sheet closes
  useEffect(() => {
    if (!mobileSelectedWebpage) {
      setMobileIsEditing(false);
      setShowMobileDeleteDialog(false);
    }
  }, [mobileSelectedWebpage]);

  // Handle click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as HTMLElement;
      
      // Don't deselect when dropdown menu is open
      if (isDropdownOpenRef.current) return;
      
      // Ignore clicks on links
      if (target.closest('a')) return;
      
      // Ignore clicks on the details panel and selected card
      if (target.closest('[data-details-panel]')) return;
      if (target.closest('[data-selected-card]')) return;
      
      // Don't deselect when clicking on dropdown trigger buttons (aria-haspopup="menu")
      if (target.closest('[aria-haspopup="menu"]')) return;
      
      // Don't deselect when clicking on dropdown menu elements (Radix portals)
      if (target.closest('[data-radix-popper-content-wrapper]')) return;
      if (target.closest('[role="menu"]')) return;
      if (target.closest('[role="menuitem"]')) return;
      if (target.closest('[role="dialog"]')) return;
      
      setSelectedWebpage(null);
      setKeyboardIndex(-1);
    }

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Reset keyboard index when filtered webpages change
  useEffect(() => {
    setKeyboardIndex(-1);
  }, [searchQuery]);

  // Select previous item after deletion (item above the deleted one)
  useEffect(() => {
    if (pendingDeleteIndexRef.current === null) return;
    if (selectedWebpage !== null) return;
    
    const deletedIndex = pendingDeleteIndexRef.current;
    
    if (filteredWebpages.length === 0) {
      setKeyboardIndex(-1);
      pendingDeleteIndexRef.current = null;
      return;
    }
    
    // Select the item that was above the deleted one (previous item)
    // If deleted item was at index 0, select the new first item
    // Use Math.min to ensure we don't go out of bounds
    const newIndex = Math.min(
      deletedIndex > 0 ? deletedIndex - 1 : 0,
      filteredWebpages.length - 1
    );
    setKeyboardIndex(newIndex);
    setSelectedWebpage(filteredWebpages[newIndex]);
    pendingDeleteIndexRef.current = null;
  }, [selectedWebpage, filteredWebpages]);

  // Keyboard navigation handler
  useEffect(() => {
    if (!isLgOrLarger) return; // Only for desktop
    
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isTyping = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
      
      // Don't handle shortcuts when typing or editing
      if (isTyping || desktopIsEditing) return;
      
      // Arrow key handling - skip when dropdown is open (Radix handles it)
      if (!desktopDropdownOpen) {
        // When selectedWebpage is set, scroll details panel instead of navigating list
        if (selectedWebpage && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
          e.preventDefault();
          const scrollContainer = document.querySelector('[data-details-scroll]');
          if (scrollContainer) {
            scrollContainer.scrollBy({ 
              top: e.key === 'ArrowDown' ? 100 : -100, 
              behavior: 'smooth' 
            });
          }
          return;
        }
        
        const webpages = filteredWebpages;
        if (webpages.length === 0) return;
        
        // Arrow Down: move to next item (preview state)
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          const newIndex = keyboardIndex < webpages.length - 1 ? keyboardIndex + 1 : keyboardIndex;
          setKeyboardIndex(newIndex);
          const webpage = webpages[newIndex];
          if (webpage && !selectedWebpage) {
            setHoveredWebpage(webpage);
          }
          // Scroll card into view
          scrollToCardAtIndex(newIndex);
        }
        
        // Arrow Up: move to previous item (preview state)
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          const newIndex = keyboardIndex > 0 ? keyboardIndex - 1 : 0;
          setKeyboardIndex(newIndex);
          const webpage = webpages[newIndex];
          if (webpage && !selectedWebpage) {
            setHoveredWebpage(webpage);
          }
          // Scroll card into view
          scrollToCardAtIndex(newIndex);
        }
        
        // Enter: promote preview to active state
        if (e.key === 'Enter') {
          e.preventDefault();
          if (keyboardIndex >= 0 && keyboardIndex < webpages.length) {
            const webpage = webpages[keyboardIndex];
            setSelectedWebpage(webpage);
            setHoveredWebpage(null);
          }
        }
        
        // Escape: deactivate selected, or clear preview
        if (e.key === 'Escape') {
          e.preventDefault();
          if (selectedWebpage) {
            // Go back to preview mode, keep keyboard index
            setSelectedWebpage(null);
            if (keyboardIndex >= 0 && keyboardIndex < webpages.length) {
              setHoveredWebpage(webpages[keyboardIndex]);
            }
          } else {
            // Clear preview
            setHoveredWebpage(null);
            setKeyboardIndex(-1);
          }
        }
      }
      
      // Action shortcuts only when a webpage is selected (active state)
      if (selectedWebpage) {
        // E: Edit
        if (e.key.toLowerCase() === 'e' && !e.metaKey && !e.ctrlKey) {
          e.preventDefault();
          setDesktopIsEditing(true);
        }
        
        // L: Copy link
        if (e.key.toLowerCase() === 'l' && !e.metaKey && !e.ctrlKey) {
          e.preventDefault();
          navigator.clipboard.writeText(selectedWebpage.url);
          toast({
            title: 'Link copied',
            description: 'URL copied to clipboard',
          });
        }
        
        // O: Toggle options dropdown
        if (e.key.toLowerCase() === 'o' && !e.metaKey && !e.ctrlKey) {
          e.preventDefault();
          setDesktopDropdownOpen(prev => !prev);
        }
        
        // T: Open in new tab
        if (e.key.toLowerCase() === 't' && !e.metaKey && !e.ctrlKey) {
          e.preventDefault();
          analytics.track('save_opened', {
            save_id: selectedWebpage.id,
            via: 'new_tab_shortcut',
          });
          window.open(selectedWebpage.url, '_blank', 'noopener,noreferrer');
        }
        
        // Cmd/Ctrl+D: Delete
        if (e.key.toLowerCase() === 'd' && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          setShowDesktopDeleteDialog(true);
        }
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isLgOrLarger, filteredWebpages, keyboardIndex, selectedWebpage, desktopIsEditing, desktopDropdownOpen, toast, scrollToCardAtIndex]);

  // Calculate keyboard hint for a card based on current navigation state
  // Returns: { hint, faded } where faded indicates reduced opacity for 4th item (index 3)
  const getKeyboardHint = (index: number, isDesktop: boolean): { hint: 'down' | 'up' | 'enter' | 'ESC'; faded: boolean } | null => {
    // Only show hints on desktop and for first 4 items (index 0-3, with index 3 faded)
    // Don't show hints when add modal is open (keyboard navigation disabled)
    if (!isDesktop || !isLgOrLarger || index > 3 || isAddModalOpen) return null;
    
    const isFaded = index === 3;
    
    // Active state: show ESC hint on selected card (to go back to list)
    if (selectedWebpage) {
      const selectedIndex = filteredWebpages.findIndex(w => w.id === selectedWebpage.id);
      if (index === selectedIndex) {
        return { hint: 'ESC', faded: isFaded };
      }
      return null;
    }
    
    // Preview state: keyboard navigation active
    if (keyboardIndex >= 0 && hoveredWebpage) {
      // Current card shows Enter
      if (index === keyboardIndex) {
        return { hint: 'enter', faded: isFaded };
      }
      // Card above shows Up arrow
      if (index === keyboardIndex - 1 && index >= 0) {
        return { hint: 'up', faded: isFaded };
      }
      // Card below shows Down arrow
      if (index === keyboardIndex + 1) {
        return { hint: 'down', faded: isFaded };
      }
      return null;
    }
    
    // Idle state: show Down arrow on first item only
    if (keyboardIndex === -1 && !hoveredWebpage && !selectedWebpage) {
      if (index === 0) {
        return { hint: 'down', faded: false };
      }
    }
    
    return null;
  };

  // Render keyboard hint with Phosphor icons
  const renderKeyboardHint = (hint: 'down' | 'up' | 'enter' | 'ESC', faded: boolean = false) => {
    const opacityClass = faded ? 'opacity-40' : '';
    
    if (hint === 'ESC') {
      return (
        <kbd className={`absolute -left-9 top-1/2 -translate-y-1/2 inline-flex h-5 px-1.5 items-center justify-center rounded bg-neutral-100 text-[10px] font-medium text-neutral-600 z-10 ${opacityClass}`}>
          ESC
        </kbd>
      );
    }
    
    const iconClass = "h-3.5 w-3.5";
    return (
      <span className={`absolute -left-7 top-1/2 -translate-y-1/2 inline-flex h-5 w-5 items-center justify-center rounded bg-neutral-100 text-neutral-600 z-10 ${opacityClass}`}>
        {hint === 'down' && <ArrowDown className={iconClass} weight="bold" />}
        {hint === 'up' && <ArrowUp className={iconClass} weight="bold" />}
        {hint === 'enter' && <ArrowElbowDownLeft className={iconClass} weight="bold" />}
      </span>
    );
  };

  // Render webpage card
  const renderWebpageCard = (webpage: Webpage, isDesktop: boolean, index: number) => {
    const hintData = getKeyboardHint(index, isDesktop);
    
    return (
      <div
        key={webpage.id}
        data-webpage-id={webpage.id}
        className="relative"
        ref={(el) => {
          if (el) {
            cardRefsMap.current.set(webpage.id, el);
          } else {
            cardRefsMap.current.delete(webpage.id);
          }
        }}
      >
        {/* Keyboard navigation hint */}
        {hintData && renderKeyboardHint(hintData.hint, hintData.faded)}
        <div
          data-selected-card={isDesktop && selectedWebpage?.id === webpage.id ? true : undefined}
          data-webpage-index={index}
          className={`w-full scroll-mt-[200px] scroll-mb-[300px] ${isDesktop ? 'md:w-[calc((768px-2rem)/2-24px-1px)] lg:w-[calc((1024px-2rem-32px-16px-32px)/2-1px)] xl:w-[calc((1280px-2rem-32px-24px-32px)/2-1px)] 2xl:w-[calc((1600px-2rem-40px-128px-32px)/2-1px)]' : ''} rounded text-card-foreground border transition-colors duration-25 ${
            isDesktop
              ? selectedWebpage?.id === webpage.id 
                ? 'border-neutral-200 bg-neutral-100' 
                : hoveredWebpage?.id === webpage.id
                ? 'bg-neutral-100/60 border-neutral-200/60'
                : 'border-transparent hover:bg-neutral-100/60 hover:border-neutral-200/60'
              : mobileSelectedWebpage?.id === webpage.id 
                ? 'border-neutral-200 bg-neutral-100'
                : 'bg-neutral-100/60 border-neutral-200/60'
          }`}
          onMouseEnter={isDesktop ? () => {
            if (!selectedWebpage) {
              setHoveredWebpage(webpage);
              setKeyboardIndex(index);
            }
          } : undefined}
          onClick={(e) => {
            if (isDesktop) {
              e.stopPropagation();
              if (selectedWebpage?.id === webpage.id) {
                setSelectedWebpage(null);
              } else {
                analytics.track('save_opened', {
                  save_id: webpage.id,
                  via: searchQuery ? 'search_result' : 'card_click',
                  position: index,
                });
                if (searchQuery) {
                  analytics.track('search_result_clicked', {
                    save_id: webpage.id,
                    position: index,
                    query_length: searchQuery.trim().length,
                  });
                }
                setSelectedWebpage(webpage);
                setHoveredWebpage(null);
                setKeyboardIndex(index);
              }
            } else {
              if (mobileSelectedWebpage?.id === webpage.id) {
                setMobileSelectedWebpage(null);
              } else {
                analytics.track('save_opened', {
                  save_id: webpage.id,
                  via: searchQuery ? 'search_result' : 'card_click_mobile',
                  position: index,
                });
                if (searchQuery) {
                  analytics.track('search_result_clicked', {
                    save_id: webpage.id,
                    position: index,
                    query_length: searchQuery.trim().length,
                  });
                }
                setMobileSelectedWebpage(webpage);
              }
            }
          }}
          role="button"
          tabIndex={0}
        >
          <div className="p-4 w-full">
            <div className="flex items-center gap-2 w-full">
              <Favicon src={webpage.favicon} storagePath={webpage.favicon_storage_path} url={webpage.url} className="h-4 w-4" />
              <h3 className="font-semibold text-neutral-800 truncate">{webpage.title}</h3>
            </div>
            <p className="mt-2 text-sm text-neutral-500 min-h-[2.5rem] line-clamp-2">
              {webpage.description}
            </p>
            <div className="mt-2 flex items-center justify-between gap-2">
              <a
                href={webpage.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline truncate min-w-0"
                onClick={(e) => {
                  e.stopPropagation();
                  analytics.track('save_opened', {
                    save_id: webpage.id,
                    via: 'external_link',
                    position: index,
                  });
                }}
              >
                {cleanUrl(webpage.url)}
              </a>
              <span className="text-xs text-neutral-400 shrink-0 whitespace-nowrap">
                {formatDistanceToNow(new Date(webpage.created_at), { addSuffix: true })}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render loading more indicator
  const renderLoadMoreTrigger = () => {
    if (searchQuery) return null; // Don't show when searching
    
    return (
      <div 
        ref={loadMoreCallbackRef} 
        className="py-8 flex justify-center"
      >
        {isLoadingMore ? (
          <div className="space-y-4 w-full">
            <WebpageCardSkeleton index={0} />
            <WebpageCardSkeleton index={1} />
          </div>
        ) : hasMore ? (
          <span className="text-sm text-neutral-400">Scroll for more...</span>
        ) : null}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar user={user} onSearch={handleSearch} onSearchFocus={handleSearchFocus} onSearchDownArrow={handleSearchDownArrow} onSignOut={signOut} isLoading={isLoading} initialSearchQuery={searchQuery} onAddModalOpenChange={setIsAddModalOpen} />
      <div 
        className="container max-w-screen-xl 2xl:max-w-[1600px] px-4 py-6"
        onMouseLeave={() => !selectedWebpage && setHoveredWebpage(null)}
      >
        {/* Two-column layout for saved webpages and details */}
        <div className="hidden lg:flex lg:gap-4 xl:gap-6 2xl:gap-32">
          {/* Left column - Webpage cards */}
          <div className="flex-1 min-w-0 space-y-4 pt-1 lg:ml-8 xl:ml-8 2xl:ml-10">
            {shouldShowListSkeleton ? (
              <>
                <div>
                  <Skeleton className="h-5 w-28" />
                </div>
                {Array.from({ length: 4 }).map((_, index) => (
                  <WebpageCardSkeleton key={index} index={index} />
                ))}
              </>
            ) : (
              <>
                <div>
                  {isCountPending ? (
                    <Skeleton className="h-5 w-28" />
                  ) : (
                    <p className="text-sm text-neutral-400">
                      {displayCount} web pages found
                    </p>
                  )}
                </div>
                {showUsageBanner && usage && usage.saves.limit !== null && (
                  <UsageBanner used={usage.saves.used} limit={usage.saves.limit} isDesktop />
                )}
                {filteredWebpages.map((webpage, index) => renderWebpageCard(webpage, true, index))}

                {/* Infinite scroll trigger - only for desktop */}
                {isLgOrLarger && renderLoadMoreTrigger()}
              </>
            )}
          </div>

          {/* Right column - Webpage details */}
          <div 
            className={`flex-1 min-w-0 overflow-visible sticky top-[5.5rem] h-[calc(100vh-5.5rem)] transition-colors duration-25 rounded-t border-t border-l border-r mt-10 lg:mr-8 xl:mr-8 2xl:mr-8 ${
              selectedWebpage ? 'border-neutral-200 bg-neutral-100' : 
              hoveredWebpage ? 'bg-neutral-100/60 border-neutral-200/60' : 
              'border-transparent'
            }`} 
            data-details-panel
          >
            <WebpageDetails 
              webpage={selectedWebpage || hoveredWebpage}
              onWebpageDeleted={handleDesktopWebpageDeleted}
              isSelected={!!selectedWebpage}
              isEditingExternal={desktopIsEditing}
              onEditingChange={setDesktopIsEditing}
              isDropdownOpenExternal={desktopDropdownOpen}
              onDropdownOpenChangeExternal={setDesktopDropdownOpen}
              onDropdownOpenChange={(open) => {
                if (open) {
                  isDropdownOpenRef.current = true;
                } else {
                  // Keep the ref true briefly to catch any delayed click events
                  setTimeout(() => {
                    isDropdownOpenRef.current = false;
                  }, 100);
                }
              }}
              isHintFaded={selectedWebpage ? filteredWebpages.findIndex(w => w.id === selectedWebpage.id) === 3 : false}
            />
          </div>
        </div>

        {/* Single column layout for smaller screens */}
        <div className="block lg:hidden space-y-4 pt-1">
          {shouldShowListSkeleton ? (
            <>
              <div>
                <Skeleton className="h-5 w-28" />
              </div>
              {Array.from({ length: 4 }).map((_, index) => (
                <WebpageCardSkeleton key={index} index={index} />
              ))}
            </>
          ) : (
            <>
              <div>
                {isCountPending ? (
                  <Skeleton className="h-5 w-28" />
                ) : (
                  <p className="text-sm text-neutral-400">
                    {displayCount} web pages found
                  </p>
                )}
              </div>
              {showUsageBanner && usage && usage.saves.limit !== null && (
                <UsageBanner used={usage.saves.used} limit={usage.saves.limit} />
              )}
              {filteredWebpages.map((webpage, index) => renderWebpageCard(webpage, false, index))}

              {/* Infinite scroll trigger - only for mobile */}
              {!isLgOrLarger && renderLoadMoreTrigger()}
            </>
          )}

          {/* Mobile Sheet */}
          <Sheet 
            open={!!mobileSelectedWebpage} 
            onOpenChange={(open) => !open && setMobileSelectedWebpage(null)}
          >
            <SheetContent 
              side={isMdOrLarger ? 'right' : 'bottom'} 
              className={isMdOrLarger ? 'w-full sm:max-w-2xl overflow-visible' : 'h-[90vh] overflow-visible'}
            >
              {mobileSelectedWebpage && (
                <>
                  <WebpageDetails 
                    webpage={mobileSelectedWebpage}
                    onWebpageDeleted={() => setMobileSelectedWebpage(null)}
                    isEditingExternal={mobileIsEditing}
                    onEditingChange={setMobileIsEditing}
                    hideMenu={true}
                    showFloatingActions={true}
                  />
                  {/* Mobile FAB for edit/delete - only show when not editing */}
                  {!mobileIsEditing && (
                    <WebpageActionsFab
                      onEdit={handleMobileEdit}
                      onDelete={handleMobileDeleteRequest}
                    />
                  )}
                </>
              )}
            </SheetContent>
          </Sheet>

          {/* Mobile delete confirmation dialog */}
          {mobileSelectedWebpage && (
            <DeleteWebpageDialog
              open={showMobileDeleteDialog}
              onOpenChange={setShowMobileDeleteDialog}
              webpageTitle={mobileSelectedWebpage.title}
              onConfirm={handleMobileDeleteConfirm}
              isDeleting={isMobileDeleting}
            />
          )}
        </div>

        <AddWebpageFab />

        {/* Desktop delete confirmation dialog (for keyboard shortcut) */}
        {selectedWebpage && (
          <DeleteWebpageDialog
            open={showDesktopDeleteDialog}
            onOpenChange={setShowDesktopDeleteDialog}
            webpageTitle={selectedWebpage.title}
            onConfirm={handleDesktopDeleteConfirm}
            isDeleting={isDesktopDeleting}
          />
        )}
      </div>

      {CHAT_ENABLED && user && (
        <ChatDock
          totalPages={totalCount}
          isMobile={!isLgOrLarger}
          initialOpen={chatFromUrlOpen}
          initialConversationId={chatConversationFromUrl}
          initialPrefill={chatPrefillQuery}
          initialPrefillRequestKey={chatPrefillRequestKey}
          onBeforeOpen={handleBeforeChatOpen}
          onOpenInAppSource={handleChatOpenInAppSource}
          onChatStateChange={handleChatStateChange}
        />
      )}
    </div>
  );
}
